import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Spin, Result } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { verifyEmail, clearError } from '../../store/authSlice';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import './AuthForms.scss';

const { Title, Text } = Typography;

const EmailVerification: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector(state => state.auth);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setVerificationStatus('error');
      return;
    }

    const verifyUserEmail = async () => {
      try {
        await dispatch(verifyEmail(token)).unwrap();
        setVerificationStatus('success');
        
        // Redirect to login after successful verification
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err) {
        setVerificationStatus('error');
        dispatch(clearError());
      }
    };

    verifyUserEmail();
  }, [token, dispatch, navigate]);

  if (verificationStatus === 'loading') {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <Title level={3} style={{ marginTop: '24px', color: '#0576b7' }}>
              Verifying Your Email...
            </Title>
            <Text style={{ color: '#6b7280' }}>
              Please wait while we verify your email address.
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <Result
            icon={<CheckCircleOutlined style={{ color: '#10b981', fontSize: '64px' }} />}
            title="Email Verified Successfully!"
            subTitle="Your email has been verified. You can now sign in to your account."
            extra={
              <div>
                <Text style={{ display: 'block', marginBottom: '16px', color: '#6b7280' }}>
                  Redirecting to login page in 3 seconds...
                </Text>
                <Button 
                  type="primary" 
                  size="large"
                  onClick={() => navigate('/login')}
                  className="auth-button"
                >
                  Sign In Now
                </Button>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <Card className="auth-card">
        <Result
          icon={<CloseCircleOutlined style={{ color: '#ef4444', fontSize: '64px' }} />}
          title="Verification Failed"
          subTitle={
            error || 
            'The verification link is invalid or has expired. Please try signing up again or request a new verification email.'
          }
          extra={
            <div>
              <Button 
                type="primary" 
                size="large"
                onClick={() => navigate('/signup')}
                className="auth-button"
                style={{ marginRight: '8px' }}
              >
                Sign Up Again
              </Button>
              <Button 
                type="default" 
                size="large"
                onClick={() => navigate('/login')}
                style={{ 
                  borderColor: '#0576b7',
                  color: '#0576b7'
                }}
              >
                Back to Login
              </Button>
            </div>
          }
        />
      </Card>
    </div>
  );
};

export default EmailVerification;